import React, {
	useState,
	useRef,
	useContext,
	type SyntheticEvent,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
} from 'react';
import type { State as PopperJSState } from '@popperjs/core';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import type DropdownMenu from '@atlaskit/dropdown-menu';
import DownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import { PopupSelect } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { getTheme } from '@atlassian/jira-common-theme';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics, AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import CustomRolesModalAsync from '@atlassian/jira-project-custom-roles/src/async.tsx';
import ReloadPageContext from '../../../controllers/reload-page-context';
import MainProviderContext from '../../../services/access-page-context';
import { customRoleInteractionMetrics } from '../../../utils/performance-analytics';
import type { RoleId } from '../../types';
import { multipleRolesMessage, getActorRoleIds, triggerAnalytics } from '../../utils';
import {
	ANALYTICS_ROLE_PICKER_DROPDOWN,
	ANALYTICS_ROLE_OPTION,
	ANALYTICS_ROLE_OPTION_MANAGE_BUTTON,
} from './constants';
import Footer from './footer';
import MenuList from './menu-list';
import messages from './messages';
import { CheckboxOption } from './option';
import type { Props, OnRoleSelect, CheckboxData } from './types';
import {
	transformProjectRolesForSelectCheckbox,
	getRoleName,
	onItemActivated,
	getDefaultPopupProps,
	getMenuListHeight,
	rolesWidth,
} from './utils';

// TODO - Once this package moved out of common we can remove disabled eslint rule

const PopupSelectWithAnalytics = AnalyticsEventToProps('button', {
	onOpen: 'clicked',
	onRoleSelect: 'clicked',
})(PopupSelect);

/**
 * NOTE: If making UI changes to this component, you may also need to make the corresponding changes to the
 * `FeatureGatedRolesPicker` (src/packages/growth/feature-gates-tmp-access/src/ui/feature-gated-roles-picker/index.tsx) for consistency.
 */
export const RolesPicker = (
	props: Props,
):
	| ReactElement<ComponentProps<typeof DropdownMenu>>
	| ReactElement<ComponentProps<typeof PopupSelect>> => {
	const {
		roles,
		actor: { roleTypeId },
		isDisabled,
		shouldFitContainer,
		addActorToRole,
		removeActorFromRole,
		intl,
		analyticsKey,
		isOpenByDefault = false,
	} = props;
	const popupRef = useRef();
	const { formatMessage } = intl;
	const actorRoleIds: RoleId[] = getActorRoleIds(props.actor);
	const reloadContext = useContext(ReloadPageContext);
	const { isProjectSimplified } = useContext(MainProviderContext);
	const { type } = props.actor;
	const [isCustomRolesAppModalOpen, setCustomRolesModalState] = useState<boolean>(false);
	const analyticsEvent = useRef<unknown>(null);

	const toggleCustomRolesApp = () => {
		setCustomRolesModalState(!isCustomRolesAppModalOpen);
		if (analyticsKey) {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			fireUIAnalytics(analyticsEvent.current.clone(), ANALYTICS_ROLE_OPTION_MANAGE_BUTTON, {
				source: analyticsKey,
			});
		}

		customRoleInteractionMetrics.start();
	};

	const onStopPerfMetric = () => customRoleInteractionMetrics.stop();

	const onClose = (
		syntheticEvent: SyntheticEvent,
		event: UIAnalyticsEvent,
		shouldAccessPageBeRefreshed: boolean,
	): void => {
		setCustomRolesModalState(!isCustomRolesAppModalOpen);
		if (shouldAccessPageBeRefreshed) {
			reloadContext.reloadPage();
		}
	};

	// keeping a pointer to button click analytics event in order to pass the same for an item click
	const renderMessage = (): string => {
		const numOfRoles = actorRoleIds.length;
		switch (numOfRoles) {
			case 0:
				return formatMessage(messages.noRolesMessage);
			case 1:
				return getRoleName(roles, actorRoleIds[0]);
			default:
				return multipleRolesMessage(intl, numOfRoles);
		}
	};

	const onRoleSelect: OnRoleSelect = (item) => {
		const operation = item.isChecked ? 'added' : 'removed';
		triggerAnalytics('party-role-changed', { operation });
		if (analyticsKey && analyticsEvent.current) {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			fireUIAnalytics(analyticsEvent.current.clone(), ANALYTICS_ROLE_OPTION, {
				source: analyticsKey,
			});
		}
		onItemActivated(
			roleTypeId,
			item,
			type,
			addActorToRole,
			removeActorFromRole,
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			analyticsEvent.current && analyticsEvent.current.clone(),
		);
	};

	const renderFooter = () => {
		const manageRoleText: string = formatMessage(messages.manageRoles);

		return isProjectSimplified ? (
			<Footer onRoleClick={toggleCustomRolesApp} text={manageRoleText} />
		) : null;
	};

	const onOpen = (evt: UIAnalyticsEvent): void => {
		if (analyticsKey) {
			analyticsEvent.current = evt;
			fireUIAnalytics(evt, ANALYTICS_ROLE_PICKER_DROPDOWN, {
				source: analyticsKey,
			});
		}
	};

	const onChange = (option: CheckboxData) => {
		onRoleSelect({
			...option,
			isChecked: !option.isChecked,
			isManageRoles: false,
		});
	};

	const getOptionLabel = (option: CheckboxData) => {
		let formattedLabel = `${option.title}, ${option.description},`;
		if (option.isChecked) {
			formattedLabel = `${formattedLabel} ${formatMessage(messages.roleSelected)},`;
		}
		if (option.isDisabled) {
			formattedLabel = `${formattedLabel} ${formatMessage(messages.roleDisabled)}, ${formatMessage(messages.roleDisabledValidation)},`;
		}
		return formattedLabel;
	};

	const options = transformProjectRolesForSelectCheckbox(roles, actorRoleIds);

	const dynamicProps = {
		components: { Option: CheckboxOption, MenuList },
		onChange,
		value: options.filter((option) => option.isChecked),
		getOptionLabel,
	};

	return (
		<div data-testid="users-and-roles.common.ui.roles-picker.dropdown">
			<PopupSelectWithAnalytics
				aria-label={intl.formatMessage(messages.selectRolesAriaLabel)}
				ref={popupRef}
				onOpen={onOpen}
				maxMenuWidth={rolesWidth}
				minMenuWidth={rolesWidth}
				// @ts-expect-error - TS7031 - Binding element 'ref' implicitly has an 'any' type. | TS7031 - Binding element 'isOpen' implicitly has an 'any' type.
				target={({ ref, isOpen }) => (
					<Tooltip
						content={renderMessage()}
						tag="div"
						testId="users-and-roles.common.ui.roles-picker.tooltip"
					>
						<CustomThemeButton
							ref={ref}
							isDisabled={isDisabled}
							shouldFitContainer={shouldFitContainer}
							theme={buttonTheme}
							isSelected={isOpen}
							iconAfter={<DownIcon label="" />}
							aria-expanded={isOpen}
						>
							{renderMessage()}
						</CustomThemeButton>
					</Tooltip>
				)}
				footer={renderFooter()}
				maxMenuHeight={getMenuListHeight(isProjectSimplified, roles.length)}
				options={options}
				popperProps={{
					placement: 'bottom-end',
					// NOTE: reason to use fixed instead of absolute to allow screen reader to read it in appropiate order.
					// `fixed` strategy react popper will render element "inside parent container" which's our intention here.
					// `absolute` strategy will render at the bottom of the DOM. (which cause screen reader to read it in wrong order, because screen reader read by traverse DOM.)
					// more details: https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/104188/overview?commentId=7280806
					strategy: 'fixed',
					modifiers: [
						{
							name: 'setZIndex',
							enabled: true,
							phase: 'read',
							fn: ({ state }: { state: PopperJSState }) => ({
								...state,
								styles: {
									...state.styles,
									popper: {
										...state.styles?.popper,
										// NOTE: if the container element e.g. Page component has zIndex 1, this will not take effect. (e.g. it would stack behind component that zIndex > 1 such as top navbar)
										// for example: https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/104188/overview?commentId=7282188
										zIndex: layers.modal - 10,
									},
								},
							}),
						},
						...[
							{
								name: 'setPosition',
								enabled: true,
								phase: 'write', // phase: https://popper.js.org/docs/v2/modifiers/#phase
								fn: ({ state }: { state: PopperJSState }) => ({
									...state,
									styles: {
										...state.styles,
										popper: {
											...state.styles?.popper,
											// using CSS position "absolute" instead of "fixed" even using fixed strategy, just to allow popper to-be visible even it's overflow viewport.
											// e.g. render close to bottom of viewport - https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/104188/overview?commentId=7282255.
											position: 'absolute',
										},
									},
								}),
							},
							{
								name: 'flip',
								enabled: false, // disabled flip, and always render with placement 'bottom-end' to avoid popper render stack under zIndex > 1 (e.g. top navbar)
							},
						],
					],
				}}
				{...dynamicProps}
				{...getDefaultPopupProps()}
				defaultIsOpen={isOpenByDefault}
			/>
			{isCustomRolesAppModalOpen && (
				// @ts-expect-error - TS2741 - Property 'FetchRolesAndPermissionsService' is missing in type '{ isOpen: true; onClose: (syntheticEvent: SyntheticEvent<Element, Event>, event: UIAnalyticsEventInterface, shouldAccessPageBeRefreshed: boolean) => void; onStopPerfMetric: () => boolean; }' but required in type '{ FetchRolesAndPermissionsService: ComponentType<Props>; }'.
				<CustomRolesModalAsync
					isOpen={isCustomRolesAppModalOpen}
					onClose={onClose}
					onStopPerfMetric={onStopPerfMetric}
				/>
			)}
		</div>
	);
};

export default injectIntl(RolesPicker);

const buttonTheme = getTheme({
	buttonStyles: {
		default: {
			textAlign: 'left',
		},
	},
});
